
import React, { useState, useEffect } from "react";
import { getMonthMatrix, formatDate, isSameDay } from "../utils/dateUtils";
import { 
  subscribeCalendarEventsByRange, 
  deleteCalendarEventById,
  cleanupOrphanContractEvents
} from "../services/calendarService";
import type { CalendarEvent } from "../types/calendar";
import { useToast } from "../components/Toast";
import { useAuth } from "../context/AuthContext";
import { CreateCalendarEventModal } from "../components/calendar/CreateCalendarEventModal";

export function CalendarPage({ onNavigate }: { onNavigate: (key: string, id: string) => void }) {
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Subscribe to events based on current view month
  useEffect(() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    const start = new Date(y, m, 1);
    const end = new Date(y, m + 1, 0); 
    
    // Buffer for grid
    start.setDate(start.getDate() - 14);
    end.setDate(end.getDate() + 14);

    const unsubscribe = subscribeCalendarEventsByRange(start, end, setEvents);
    return () => unsubscribe();
  }, [currentDate]);

  // Matrix generation
  const matrix = getMonthMatrix(currentDate.getFullYear(), currentDate.getMonth());

  const getEventsForDay = (day: Date) => {
    const dayStr = formatDate(day);
    return events.filter(e => {
        // Simple string comparison for now as service returns expanded events
        if (e.mode === 'TIME_RANGE' || !e.startDate) {
            return isSameDay(e.date || new Date(), day);
        }
        if (e.startDate && e.endDate) {
            return dayStr >= e.startDate && dayStr <= e.endDate;
        }
        return false;
    });
  };

  const selectedEvents = getEventsForDay(selectedDate);

  const handleDelete = async (e: CalendarEvent) => {
    if (e.sourceType !== 'manual') {
      alert("系統自動產生的事件無法直接刪除，請修改來源資料（如合約或案件狀態）。");
      return;
    }
    if (!confirm("確定刪除此事件？(若是重複事件，將刪除整個系列)")) return;
    
    try {
      await deleteCalendarEventById(e.id.split('_')[0]); // Use real ID stripping virtual suffix
      showToast("已刪除");
    } catch (err) {
      showToast("刪除失敗", "error");
    }
  };

  const handleEventClick = (e: CalendarEvent) => {
    const map: Record<string, string> = {
      'contract': 'contracts',
      'contractReview': 'contractReviews',
      'case': 'legalMatters',
      'legal-matter': 'legalMatters',
      'ip-matter': 'ipMatters',
      'brandAsset': 'brandAssets'
    };
    if (map[e.sourceType] && e.sourceId) {
      onNavigate(map[e.sourceType], e.sourceId);
    }
  };

  const getEventColor = (e: CalendarEvent) => {
      if (e.color) return e.color;
      
      // Auto Type Coloring
      if (e.autoType === 'contract-sign-date') return '#10b981'; // Green
      if (e.autoType === 'contract-expiry-0') return '#ef4444'; // Red
      if (e.autoType === 'contract-expiry-30' || e.autoType === 'contract-expiry-60') return '#f59e0b'; // Orange
      if (e.autoType === 'contract-review-deadline') return '#f97316';

      // Fallback
      if (e.sourceType === 'manual') return '#666';
      
      return '#444';
  };

  // Safe time extractor helper
  const getSafeTimeStr = (isoStr?: string) => {
      if (!isoStr || typeof isoStr !== 'string') return '';
      const parts = isoStr.split('T');
      if (parts.length < 2) return '';
      return parts[1].slice(0, 5);
  };

  // Run cleanup once on mount (optional maintenance)
  useEffect(() => {
      // cleanupOrphanContractEvents(); 
  }, []);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <button className="smallbtn" onClick={() => { setCurrentDate(new Date()); setSelectedDate(new Date()); }}>今天</button>
        <div className="row" style={{ alignItems: "center", gap: 12 }}>
          <button className="smallbtn" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}>&lt;</button>
          <div style={{ fontWeight: "bold", fontSize: 18, minWidth: 120, textAlign: "center" }}>
            {currentDate.getFullYear()}年 {currentDate.getMonth() + 1}月
          </div>
          <button className="smallbtn" onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}>&gt;</button>
        </div>
        <button className="smallbtn" style={{ background: "#fff", color: "#000", fontWeight: "bold" }} onClick={() => setIsAddModalOpen(true)}>
          + 新增事件
        </button>
      </div>

      <div className="calendar-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16, flex: 1, overflow: 'hidden' }}>
        {/* Calendar Grid */}
        <div className="calendar-main" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div className="calendar-header-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: 8 }}>
            {["日", "一", "二", "三", "四", "五", "六"].map((d, i) => (
                <div key={d} style={{ color: i===0||i===6 ? "#fbbf24" : "var(--muted)" }}>{d}</div>
            ))}
          </div>
          <div className="calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, flex: 1, overflow: 'hidden' }}>
            {matrix.flat().map((cell) => {
              const dayEvents = getEventsForDay(cell.date);
              const isSelected = isSameDay(selectedDate, cell.date);
              
              return (
                <div 
                  key={cell.dateStr}
                  onClick={() => setSelectedDate(cell.date)}
                  style={{
                    background: isSelected ? 'rgba(59, 130, 246, 0.2)' : '#222',
                    border: isSelected ? '1px solid #3b82f6' : '1px solid #333',
                    borderRadius: 6,
                    padding: 4,
                    opacity: cell.isCurrentMonth ? 1 : 0.4,
                    cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', gap: 2,
                    minHeight: 0, overflow: 'hidden'
                  }}
                >
                  <div style={{ textAlign: 'right', fontSize: 12, color: cell.isToday ? 'var(--ok)' : (cell.isHoliday ? '#fbbf24' : '#aaa'), fontWeight: cell.isToday ? 'bold' : 'normal' }}>
                    {cell.day}
                  </div>
                  <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {dayEvents.slice(0, 3).map((ev) => {
                      const timeLabel = ev.mode === 'TIME_RANGE' ? getSafeTimeStr(ev.startDateTime) + ' ' : '';
                      return (
                        <div key={ev.id} style={{ fontSize: 10, background: getEventColor(ev), color: '#fff', borderRadius: 2, padding: '1px 3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {timeLabel}
                          {ev.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && <div style={{ fontSize: 10, color: '#aaa', textAlign: 'center' }}>+{dayEvents.length - 3}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar List */}
        <div className="calendar-sidebar" style={{ background: '#1a1d24', border: '1px solid var(--border)', borderRadius: 12, display: 'flex', flexDirection: 'column' }}>
           <div style={{ padding: '16px', borderBottom: '1px solid #333', background: '#222', borderRadius: '12px 12px 0 0' }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>{formatDate(selectedDate)}</h3>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{selectedEvents.length} 筆行程</div>
           </div>
           <div style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {selectedEvents.length === 0 && <div style={{ color: '#666', textAlign: 'center', marginTop: 20 }}>無行程</div>}
              
              {selectedEvents.map(ev => {
                  const startTime = getSafeTimeStr(ev.startDateTime);
                  const endTime = getSafeTimeStr(ev.endDateTime);
                  
                  return (
                    <div key={ev.id} style={{ background: '#2a2a2a', padding: 10, borderRadius: 6, borderLeft: `3px solid ${getEventColor(ev)}` }}>
                       <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>
                          {ev.mode === 'TIME_RANGE' && startTime && endTime && (
                              <div style={{fontSize: 11, color: '#fbbf24', marginBottom: 2}}>
                                  {startTime} - {endTime}
                              </div>
                          )}
                          {ev.title}
                       </div>
                       <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="badge" style={{ fontSize: 10 }}>{ev.sourceType === 'manual' ? '手動' : '系統'}</span>
                          <div style={{ display: 'flex', gap: 6 }}>
                             {ev.sourceType !== 'manual' && <button className="smallbtn" style={{ padding: '2px 6px', fontSize: 10 }} onClick={() => handleEventClick(ev)}>前往</button>}
                             {ev.sourceType === 'manual' && <button className="smallbtn" style={{ padding: '2px 6px', fontSize: 10, color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => handleDelete(ev)}>刪除</button>}
                          </div>
                       </div>
                       {ev.note && <div style={{marginTop: 6, fontSize: 12, color: '#999', whiteSpace: 'pre-wrap'}}>{ev.note}</div>}
                    </div>
                  );
              })}
           </div>
        </div>
      </div>

      {isAddModalOpen && (
        <CreateCalendarEventModal 
            onClose={() => setIsAddModalOpen(false)} 
            onSuccess={() => setIsAddModalOpen(false)} 
        />
      )}
    </div>
  );
}
0